/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { COLORS, formatNumber } from '@marsinet/commons';
import i18next from 'i18next';
import { MarsinetContext } from '../../context/marsinetProvider';
import { formatUtcTimestamp } from '../../utils/timezone-utils';

export interface AreaChartCustomProps {
  data: any[];
  title: string;
  setLoading: (loading: boolean) => void;
  chartOptions?: {
    type?: 'monotone' | 'stepAfter';
    yAxisDomain?: [number, number];
    tickCount?: number;
    yAxisUnit?: string;
    tickFormatter?: (value: number) => string;
  };
  highlightedPoints?: {
    timestamp: string;
    info: {
      eventType: string;
      eventId: string | null;
      params: string | null;
      value: number;
    };
  }[];
}

const CustomTooltip = ({
  active,
  payload,
  label,
  visibleLines,
  t,
  alarmInfo,
  timeZone,
  locale,
}) => {
  if (!active && !alarmInfo) return null;

  const filteredPayload = payload.filter((entry) => visibleLines[entry.name]);

  if (filteredPayload.length === 0) {
    return null;
  }

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        padding: '10px',
        maxWidth: '250px',
      }}
    >
      {alarmInfo ? (
        <div>
          <p
            style={{
              fontWeight: 'bold',
              color: alarmInfo.info.eventType !== 2 ? 'red' : 'blue',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            {alarmInfo.info.eventType !== 2 ? 'Alarm' : 'Test'}
          </p>
          {alarmInfo.info &&
            Object.entries(alarmInfo.info).map(([key, value], index) => (
              <p key={`${key}-${index}`} style={{ color: 'black' }}>
                <span style={{ fontWeight: 600 }}>
                  {`${t(`pages.alarms.${key}`)}:`}
                </span>{' '}
                {value == null
                  ? t('pages.alarms.noParams')
                  : String(value)}{' '}
              </p>
            ))}
        </div>
      ) : (
        <>
          <p
            className="label"
            style={{
              fontWeight: 'bold',
              fontSize: '15px',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            {timeZone && locale && label
              ? formatUtcTimestamp(label, timeZone, locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })
              : `${label}`}
          </p>
          {filteredPayload.map((entry, index) => (
            <p key={`${entry.name}-${index}`} style={{ color: entry.stroke }}>
              {`${t(`pages.maintenance.ingestion.graphs.${entry.name}`)} : `}
              <span style={{ fontWeight: 'bold' }}>
                {formatNumber(entry.value, i18next.language)}
              </span>
            </p>
          ))}
        </>
      )}
    </div>
  );
};

export function AreaChartCustom(props: Readonly<AreaChartCustomProps>) {
  const {
    data,
    title,
    setLoading,
    chartOptions = {},
    highlightedPoints = [],
  } = props;
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);

  const [hoveredAlarm, setHoveredAlarm] = useState(null);

  const keys = useMemo(
    () =>
      data.length > 0
        ? Object.keys(data[0])
            .filter((key) => key !== 'timestamp')
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 5)
        : [],
    [data]
  );

  const [visibleLines, setVisibleLines] = useState(() =>
    Object.fromEntries(keys.map((key) => [key, true]))
  );

  useEffect(() => {
    setVisibleLines((prevState) => {
      const updatedState = Object.fromEntries(
        keys.map((key) => [
          key,
          prevState[key] !== undefined ? prevState[key] : true,
        ])
      );
      return updatedState;
    });
    setLoading(false);
  }, [keys, setLoading]);

  const handleLegendClick = useCallback((payload) => {
    const { dataKey } = payload;
    setVisibleLines((prevState) => ({
      ...prevState,
      [dataKey]: !prevState[dataKey],
    }));
  }, []);

  const renderLegend = useCallback(
    (value, entry) => {
      const { dataKey } = entry.payload;
      return (
        <span style={{ color: visibleLines[dataKey] ? null : 'gray' }}>
          {t(`pages.maintenance.ingestion.graphs.${value}`)}
        </span>
      );
    },
    [visibleLines, t]
  );

  const generateGradients = useCallback((keys) => {
    return keys.map((key, index) => (
      <linearGradient key={key} id={`color${key}`} x1="0" y1="0" x2="0" y2="1">
        <stop
          offset="5%"
          stopColor={COLORS[index % COLORS.length]}
          stopOpacity={0.8}
        />
        <stop
          offset="95%"
          stopColor={COLORS[index % COLORS.length]}
          stopOpacity={0}
        />
      </linearGradient>
    ));
  }, []);

  const generateAreas = useCallback(
    (keys, visibleLines) => {
      return keys.map((key, index) => (
        <Area
          key={key}
          type={chartOptions?.type || 'monotone'}
          dataKey={key}
          stroke={COLORS[index % COLORS.length]}
          strokeOpacity={visibleLines[key] ? 1 : 0}
          fill={visibleLines[key] ? `url(#color${key})` : 'none'}
          strokeWidth={2}
          connectNulls={true}
        />
      ));
    },
    [visibleLines, chartOptions]
  );

  const anyVisibleLines = Object.values(visibleLines).some(
    (visible) => visible
  );

  return (
    <div>
      <div className="text-2xl font-bold pl-2">{title}</div>
      <ResponsiveContainer width="100%" height={480} className="pl-2">
        <AreaChart
          data={data}
          syncId="anyId"
          margin={{
            top: 15,
            right: 30,
            bottom: 50,
          }}
        >
          <defs>{generateGradients(keys)}</defs>
          <CartesianGrid strokeDasharray="3 3" />
          {/* Los datos llegan en UTC; las etiquetas usan la zona seleccionada. */}
          <XAxis
            dataKey="timestamp"
            tickFormatter={(timestamp) =>
              formatUtcTimestamp(timestamp, selectedTimezone, i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
            }
            tick={{ fontSize: 14 }}
          />
          <YAxis
            domain={chartOptions?.yAxisDomain || ['auto', 'auto']}
            tickCount={chartOptions?.tickCount || undefined}
            tickFormatter={chartOptions?.tickFormatter || ((value) => value)}
            tick={{ fontSize: 14 }}
            width={120} // ← da espacio para textos largos
          />
          <Legend onClick={handleLegendClick} formatter={renderLegend} />
          {anyVisibleLines && (
            <Tooltip
              content={
                <CustomTooltip
                  active={undefined}
                  payload={undefined}
                  label={undefined}
                  visibleLines={visibleLines}
                  t={t}
                  alarmInfo={hoveredAlarm}
                  timeZone={selectedTimezone}
                  locale={i18n.language}
                />
              }
              cursor={{
                stroke: 'black',
                strokeWidth: 2,
                strokeDasharray: '5 5',
              }}
            />
          )}
          {generateAreas(keys, visibleLines)}
          {highlightedPoints.map((alarm: any, index) => (
            <g key={index}>
              {/* Línea de referencia visible */}
              <ReferenceLine
                x={alarm.timestamp}
                stroke={alarm.info.eventType !== 2 ? 'red' : 'blue'}
                strokeWidth={2}
                strokeDasharray="5 5"
              />

              {/* Línea de referencia invisible que captura eventos */}
              <ReferenceLine
                x={alarm.timestamp}
                stroke="transparent"
                strokeWidth={8}
                opacity={0.05}
                onMouseEnter={() => setHoveredAlarm(alarm)}
                onMouseLeave={() => setHoveredAlarm(null)}
              />
            </g>
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default AreaChartCustom;
